/**
 * RegionSelector Component
 * 
 * Overlay for selecting a region on the scanned image for re-OCR.
 * Supports:
 * - Click and drag to select rectangle
 * - Visual feedback during selection
 * - Normalized coordinates (0-1) for any image size
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Crosshair, X } from 'lucide-react';
import type { RegionSelection } from './types';

// ============================================================================
// Types
// ============================================================================

interface RegionSelectorProps {
  imageUrl: string;
  pageIndex: number;
  isActive: boolean;
  onRegionSelected: (region: RegionSelection) => void;
  onCancel: () => void;
}

interface SelectionState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  isSelecting: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function RegionSelector({
  imageUrl,
  pageIndex,
  isActive,
  onRegionSelected,
  onCancel,
}: RegionSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Track image dimensions for coordinate normalization
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  }, []);

  // Get normalized coordinates (0-1) from mouse event
  const getNormalizedCoords = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    
    return { x, y };
  }, []);

  // Mouse down - start selection
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isActive) return;
    e.preventDefault();
    
    const { x, y } = getNormalizedCoords(e);
    setSelection({
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
      isSelecting: true,
    });
  }, [isActive, getNormalizedCoords]);

  // Mouse move - update selection
  useEffect(() => {
    if (!selection?.isSelecting) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { x, y } = getNormalizedCoords(e);
      setSelection(prev => prev ? { ...prev, currentX: x, currentY: y } : null);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!selection) return;
      
      const { x, y } = getNormalizedCoords(e);
      
      // Calculate normalized region
      const minX = Math.min(selection.startX, x);
      const minY = Math.min(selection.startY, y);
      const maxX = Math.max(selection.startX, x);
      const maxY = Math.max(selection.startY, y);
      
      const width = maxX - minX;
      const height = maxY - minY;
      
      // Only trigger if selection is meaningful (> 2% of image in each dimension)
      if (width > 0.02 && height > 0.02) {
        onRegionSelected({
          x: minX,
          y: minY,
          width,
          height,
          pageIndex,
        });
      }
      
      setSelection(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [selection, getNormalizedCoords, onRegionSelected, pageIndex]);

  // Calculate selection rectangle style
  const getSelectionStyle = () => {
    if (!selection) return {};
    
    const minX = Math.min(selection.startX, selection.currentX);
    const minY = Math.min(selection.startY, selection.currentY);
    const width = Math.abs(selection.currentX - selection.startX);
    const height = Math.abs(selection.currentY - selection.startY);
    
    return {
      left: `${minX * 100}%`,
      top: `${minY * 100}%`,
      width: `${width * 100}%`,
      height: `${height * 100}%`,
    };
  };

  // Escape key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        onCancel();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, onCancel]);

  return (
    <div className="relative w-full h-full">
      {/* Image */}
      <img
        src={imageUrl}
        alt="Scanned page"
        onLoad={handleImageLoad}
        className="w-full h-full object-contain"
      />
      
      {/* Selection Overlay */}
      {isActive && (
        <div
          ref={containerRef}
          className="absolute inset-0 cursor-crosshair"
          onMouseDown={handleMouseDown}
        >
          {/* Dim overlay when active */}
          <div className="absolute inset-0 bg-black/30" />
          
          {/* Selection rectangle */}
          {selection && (
            <div
              className="absolute border-2 border-[#0A84FF] bg-[#0A84FF]/20"
              style={getSelectionStyle()}
            >
              {/* Corner handles */}
              <div className="absolute -top-1 -left-1 w-2 h-2 bg-[#0A84FF] rounded-full" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#0A84FF] rounded-full" />
              <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-[#0A84FF] rounded-full" />
              <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-[#0A84FF] rounded-full" />
            </div>
          )}
          
          {/* Instructions overlay */}
          {!selection && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black/70 backdrop-blur-sm px-4 py-3 rounded-xl text-center">
                <Crosshair className="w-6 h-6 text-[#0A84FF] mx-auto mb-2" />
                <div className="text-[#1A1A1A] text-sm font-medium">
                  Click and drag to select region
                </div>
                <div className="text-[#888888] text-xs mt-1">
                  Press Esc to cancel
                </div>
              </div>
            </div>
          )}
          
          {/* Cancel button */}
          <button
            onClick={onCancel}
            className="absolute top-2 right-2 p-2 rounded-lg bg-black/50 hover:bg-black/70 text-[#3D3D3D] hover:text-white transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Crop an image to a specific region and return as base64.
 * Used before sending to AI Vision for re-OCR.
 */
export async function cropImageRegion(
  imageUrl: string,
  region: RegionSelection
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      // Calculate pixel coordinates from normalized values
      const x = Math.round(region.x * img.naturalWidth);
      const y = Math.round(region.y * img.naturalHeight);
      const width = Math.round(region.width * img.naturalWidth);
      const height = Math.round(region.height * img.naturalHeight);
      
      // Set canvas size to cropped region
      canvas.width = width;
      canvas.height = height;
      
      // Draw cropped region
      ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
      
      // Convert to base64 (without data URL prefix for AI Vision)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
      
      resolve(base64);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for cropping'));
    };
    
    img.src = imageUrl;
  });
}

export default RegionSelector;
